import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { teamApi } from "@/api/team";
import { authApi } from "@/api/auth";
import { useSession } from "@/stores/session";
import { ApiError } from "@/api/types";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/password-input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/layout/auth-shell";

export const Route = createFileRoute("/accept-invitation")({
  component: AcceptInvitationPage,
});

function AcceptInvitationPage() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await teamApi.accept(token, password);
      // The accept response sets a session cookie; hydrate the session store
      // from /me so the app has the full user + environment, then enter.
      const me = await authApi.me();
      useSession.getState().setSession(me);
      void nav({ to: "/" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) setErr("This invitation is invalid or has expired.");
      else setErr("Could not accept the invitation. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Accept invitation" subtitle="Join a deleggate team.">
        <p role="alert" className="text-sm text-fail">
          This link is missing its invitation token. Use the exact link from your invite email.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Accept invitation" subtitle="Set a password to join the team.">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="password">Choose a password</Label>
          <PasswordInput
            id="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {err && (
          <p role="alert" className="text-sm text-fail">
            {err}
          </p>
        )}
        <Button
          type="submit"
          className="w-full bg-brand text-primary-foreground hover:bg-brand/90"
          disabled={loading}
        >
          {loading ? "Joining…" : "Join team"}
        </Button>
      </form>
    </AuthShell>
  );
}
